// Questionnaire guidé du bilan gratuit MAI — mobile-first, une question par écran.
// Chaque option peut porter un micro-retour contextuel de MAI (feedback court et utile).

export const RACE_GOALS = ["5km", "10km", "semi", "marathon", "trail", "autre"];

// Mise en sommeil trail : on FILTRE dynamiquement selon les feature flags (aucune suppression du
// questionnaire — les champs réapparaissent automatiquement si trail est réactivé).
export function buildRaceGoals(features = {}) {
  return features.trail_enabled ? RACE_GOALS : RACE_GOALS.filter((g) => g !== "trail");
}

export function buildQuestions(features = {}) {
  const trail = !!features.trail_enabled;
  if (trail) return QUESTIONS;
  return QUESTIONS.map((q) => {
    if (q.id === "goal") {
      const options = q.options.filter((o) => o.value !== "trail");
      const feedback = { ...(q.feedback || {}) };
      delete feedback.trail;
      return { ...q, options, feedback };
    }
    if (q.type === "group" && (q.fields || []).some((f) => f.id === "trail_experience")) {
      return { ...q, fields: q.fields.filter((f) => f.id !== "trail_experience") };
    }
    return q;
  });
}

export const QUESTIONS = [
  {
    id: "goal",
    title: "Quel est ton objectif principal ?",
    subtitle: "MAI construit ta préparation autour de lui.",
    type: "single",
    options: [
      { value: "remise_en_forme", label: "Remise en forme", hint: "Reprendre le sport et me sentir mieux" },
      { value: "5km", label: "5 km", hint: "Aller vite sur une distance courte" },
      { value: "10km", label: "10 km", hint: "La distance polyvalente par excellence" },
      { value: "semi", label: "Semi-marathon", hint: "21,1 km, un vrai défi d'endurance" },
      { value: "marathon", label: "Marathon", hint: "42,2 km, le grand objectif" },
      { value: "trail", label: "Trail", hint: "Courir en nature, sur les sentiers" },
      { value: "autre", label: "Autre distance", hint: "Une distance précise (ex. 17 km)" },
    ],
    feedback: {
      marathon: "Bel objectif. Le marathon demande de la patience — MAI va construire une montée en charge progressive.",
      semi: "Le semi est parfait pour progresser sérieusement sans y passer tout ton temps.",
      remise_en_forme: "On part sur du régulier et du plaisir. MAI cale une progression douce, sans te brusquer.",
      trail: "Le trail mêle endurance et dénivelé — MAI adaptera le travail spécifique.",
      autre: "Parfait : indique la distance exacte que tu prépares — MAI raisonne sur cette distance, sans l'arrondir.",
    },
  },
  {
    id: "custom_distance_km",
    title: "Quelle distance prépares-tu ?",
    subtitle: "Indique la distance exacte en kilomètres — MAI la garde telle quelle.",
    type: "number",
    unit: "km",
    placeholder: "Ex : 17",
    min: 1,
    max: 300,
    step: 0.1,
    showIf: (a) => a.goal === "autre",
    feedbackFn: (val) => (val ? `Noté : objectif de ${val} km. MAI compare cette exigence à ta capacité actuelle pour décider quoi développer.` : ""),
  },
  {
    id: "race_date",
    title: "As-tu une date de course ?",
    subtitle: "Si oui, MAI cale toute ta préparation jusqu'au jour J.",
    type: "date",
    showIf: (a) => RACE_GOALS.includes(a.goal),
    skipLabel: "Pas encore de date",
    feedbackFn: (val) => (val ? "Parfait, MAI va périodiser ta prépa (développement → spécifique → affûtage) jusqu'à cette date." : "Aucun souci, MAI construit un bloc de progression que tu pourras dater plus tard."),
  },
  {
    id: "level",
    title: "Comment décrirais-tu ton niveau ?",
    subtitle: "Sois honnête, ça permet à MAI de bien doser.",
    type: "single",
    options: [
      { value: "debutant", label: "Débutant", hint: "Je débute ou je cours depuis peu" },
      { value: "intermediaire", label: "Intermédiaire", hint: "Je cours régulièrement" },
      { value: "avance", label: "Avancé / confirmé", hint: "J'ai déjà un bon volume et de l'expérience" },
    ],
    feedback: {
      debutant: "MAI privilégiera la régularité et la récupération : c'est là que se construisent les progrès.",
      avance: "On pourra intégrer du travail qualitatif et une vraie planification par phases.",
    },
  },
  {
    id: "experience",
    title: "Où en es-tu dans ta pratique ?",
    subtitle: "",
    type: "single",
    options: [
      { value: "moins_6mois", label: "Moins de 6 mois", hint: "Je découvre encore la course" },
      { value: "6_24mois", label: "6 mois à 2 ans", hint: "Je cours depuis un moment" },
      { value: "plus_2ans", label: "Plus de 2 ans", hint: "La course fait partie de ma vie" },
      { value: "reprise", label: "Je reprends après une coupure", hint: "Blessure, pause, vie perso…" },
    ],
    feedback: {
      reprise: "MAI va prévoir une reprise progressive : on remonte le volume en douceur pour éviter la blessure.",
    },
  },
  {
    id: "frequency",
    title: "Combien de fois par semaine peux-tu courir ?",
    subtitle: "MAI s'adapte à ton emploi du temps réel.",
    type: "scale",
    options: [
      { value: 2, label: "2" },
      { value: 3, label: "3" },
      { value: 4, label: "4" },
      { value: 5, label: "5" },
      { value: 6, label: "6" },
    ],
    feedback: {
      2: "2 sorties bien construites suffisent déjà pour progresser. MAI les rendra complémentaires.",
      3: "3 sorties par semaine, c'est l'équilibre idéal pour progresser sans te blesser.",
      4: "4 sorties permettent une vraie variété : endurance, qualité et sortie longue.",
      5: "Beau volume ! MAI veillera à bien alterner intensité et récupération.",
      6: "Volume élevé — MAI protègera ta récupération pour que ça reste durable.",
    },
  },
  {
    id: "max_session_duration",
    title: "Quel est le temps maximum que tu peux consacrer à une séance ?",
    subtitle: "Il s'agit de ton temps DISPONIBLE, pas nécessairement de la durée que tu cours déjà aujourd'hui. C'est une limite haute, pas un objectif.",
    type: "single",
    options: [
      { value: 30, label: "30 min", hint: "Créneaux courts" },
      { value: 45, label: "45 min", hint: "Format classique" },
      { value: 60, label: "60 min", hint: "1 heure disponible" },
      { value: 75, label: "75 min", hint: "Un peu plus de marge" },
      { value: 90, label: "90 min", hint: "Grandes séances possibles" },
      { value: 120, label: "90 min et +", hint: "Aucune contrainte de temps" },
    ],
    feedback: {
      30: "Noté : MAI construira des séances efficaces qui tiennent dans 30 min — jamais plus.",
      120: "Belle marge : MAI gardera la liberté de proposer des sorties longues quand c'est utile (sans jamais t'imposer la durée max).",
    },
  },
  {
    id: "pratique_recente",
    title: "Ta pratique récente",
    subtitle: "Ta réalité des dernières semaines — pas tes objectifs. « Je ne sais pas » est une réponse valable.",
    type: "group",
    fields: [
      { id: "recent_sessions_per_week", label: "Ces dernières semaines, tu as réellement couru…",
        options: [
          { value: "0", label: "Pas du tout" }, { value: "lt1", label: "Moins d'1x/sem" },
          { value: "1", label: "1x/sem" }, { value: "2", label: "2x/sem" },
          { value: "3", label: "3x/sem" }, { value: "4plus", label: "4x ou +" },
        ] },
      { id: "recent_weekly_volume", label: "Temps total couru sur une semaine habituelle récente",
        options: [
          { value: "lt30", label: "< 30 min" }, { value: "30_60", label: "30–60 min" },
          { value: "60_120", label: "1–2 h" }, { value: "120_180", label: "2–3 h" },
          { value: "gt180", label: "+ de 3 h" }, { value: "unknown", label: "Je ne sais pas" },
        ] },
      { id: "continuous_capacity_min", label: "Combien de temps peux-tu courir sans t'arrêter, à l'aise ?",
        options: [
          { value: "0", label: "Je ne cours pas encore" }, { value: "10", label: "~10 min" },
          { value: "20", label: "~20 min" }, { value: "30", label: "~30 min" },
          { value: "45", label: "~45 min" }, { value: "60plus", label: "1 h ou +" },
          { value: "unknown", label: "Je ne sais pas" },
        ] },
      { id: "recent_longest_run_min", label: "Au cours des 4 dernières semaines, ta plus longue sortie réellement effectuée",
        options: [
          { value: "lt20", label: "< 20 min" }, { value: "20_40", label: "20–40 min" },
          { value: "40_60", label: "40–60 min" }, { value: "60_90", label: "1 h–1 h 30" },
          { value: "gt90", label: "+ d'1 h 30" }, { value: "unknown", label: "Je ne sais pas" },
        ] },
      { id: "external_load", label: "Autres sports pratiqués chaque semaine (facultatif)", type: "multi",
        options: [
          { value: "musculation", label: "Musculation" }, { value: "velo", label: "Vélo" },
          { value: "natation", label: "Natation" }, { value: "sport_co", label: "Sport co" },
          { value: "cours_co", label: "Cours collectifs" }, { value: "autre", label: "Autre" },
        ] },
    ],
  },
  {
    id: "experience_run",
    title: "Ton expérience",
    subtitle: "Pour doser l'intensité sans risque.",
    type: "group",
    fields: [
      { id: "intervals_experience", label: "As-tu déjà pratiqué régulièrement des séances avec accélérations / fractionné ?",
        options: [
          { value: "jamais", label: "Jamais" }, { value: "occasionnel", label: "Occasionnellement" },
          { value: "regulier", label: "Régulièrement" },
        ] },
      { id: "hills_experience", label: "As-tu l'habitude de courir en côtes / dénivelé ?",
        options: [
          { value: "jamais", label: "Jamais" }, { value: "un_peu", label: "Un peu" },
          { value: "regulier", label: "Régulièrement" },
        ] },
      { id: "trail_experience", label: "As-tu déjà couru sur sentiers / en nature ?",
        showIf: (a) => a.goal === "trail",
        options: [
          { value: "jamais", label: "Jamais" }, { value: "quelques", label: "Quelques sorties" },
          { value: "regulier", label: "Régulièrement" },
        ] },
    ],
  },
  {
    id: "reprise_sante",
    title: "Reprise & santé",
    subtitle: "Pour rester prudent — ce n'est pas un avis médical.",
    type: "group",
    fields: [
      { id: "stop_recent", label: "As-tu arrêté de courir récemment ?",
        options: [
          { value: "non", label: "Non" }, { value: "oui_court", label: "Oui, moins d'1 mois" },
          { value: "oui_long", label: "Oui, plus d'1 mois" },
        ] },
      { id: "resumed", label: "Tu as déjà recommencé à courir ?",
        showIf: (a) => a.stop_recent === "oui_court" || a.stop_recent === "oui_long",
        options: [{ value: "oui", label: "Oui" }, { value: "non", label: "Pas encore" }] },
      { id: "active_pain", label: "Une douleur te gêne-t-elle actuellement quand tu cours ?",
        options: [
          { value: "non", label: "Non" }, { value: "un_peu", label: "Un peu" },
          { value: "oui", label: "Oui" },
        ] },
    ],
  },
  {
    id: "condition",
    title: "Où en es-tu physiquement en ce moment ?",
    subtitle: "Pour que MAI démarre au bon rythme, sans te brusquer.",
    type: "single",
    options: [
      { value: "forme", label: "Plutôt en forme", hint: "L'effort ne me fait pas peur" },
      { value: "moyenne", label: "Un peu rouillé·e", hint: "Vite essoufflé·e, mais ça va" },
      { value: "eloignee", label: "Loin de ma forme", hint: "Le moindre effort me demande beaucoup en ce moment" },
    ],
    feedback: {
      eloignee: "Merci de ta franchise. MAI va démarrer tout en douceur et faire monter la charge très progressivement — l'important, c'est de durer.",
      moyenne: "Parfait : on reprend les bases tranquillement et on construit à partir de là.",
    },
  },
  {
    id: "vma",
    title: "Connais-tu ta VMA ?",
    subtitle: "Optionnel — ça permet des allures précises. Sinon MAI part du ressenti.",
    type: "vma",
    skipLabel: "Je ne la connais pas",
    feedbackFn: (val) => (val ? `MAI calera tes allures sur ${val} km/h. Précis et personnalisé.` : "Aucun problème : MAI démarre au ressenti et te proposera un test VMA au bon moment."),
  },
  {
    id: "pains",
    title: "Des zones sensibles à surveiller ?",
    subtitle: "MAI en tiendra compte pour rester prudent.",
    type: "multi",
    options: [
      { value: "genou", label: "Genou" },
      { value: "mollet", label: "Mollet" },
      { value: "cheville", label: "Cheville" },
      { value: "hanche", label: "Hanche" },
      { value: "dos", label: "Dos" },
      { value: "tendon", label: "Tendon d'Achille" },
    ],
    noneLabel: "Aucune, tout va bien",
  },
];
